<template>
    <!-- Solo cuando el usuario pertenece a >1 empresa. Un único b-dropdown
         (patrón estándar de la app) resuelve ambos modos del sidebar:
         expandido = combobox con nombre; colapsado = ícono que abre el panel
         con las empresas seleccionables. -->
    <b-dropdown
        v-if="hasMultipleCompanies"
        class="company-dropdown"
        :class="{ 'is-collapsed': !expanded }"
        :position="expanded ? 'is-top-left' : 'is-top-right'"
        :mobile-modal="false"
        aria-role="list"
    >
        <template #trigger="{ active }">
            <button
                type="button"
                class="company-trigger"
                v-tooltip="!expanded ? { content: triggerTooltip, theme: 'sidebar-tooltip' } : ''"
                :aria-label="triggerTooltip"
            >
                <span class="company-glyph">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                </span>
                <span v-if="expanded" class="company-name">{{ activeCompanyName }}</span>
                <ArrowDown
                    v-if="expanded"
                    class="company-chevron"
                    :class="{ 'is-open': active }"
                />
            </button>
        </template>

        <b-dropdown-item
            v-for="company in companies"
            :key="company.id"
            :value="company.id"
            aria-role="listitem"
            class="company-item"
            :class="{ 'is-active': isActive(company) }"
            @click="select(company)"
        >
            <span class="company-item-name">{{ company.name }}</span>
            <Tick v-if="isActive(company)" class="company-item-check" />
        </b-dropdown-item>
    </b-dropdown>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import Tick from "@/components/svg/Tick.vue";

export default {
    name: "SidebarCompanySelect",
    components: { ArrowDown, Tick },
    props: {
        expanded: { type: Boolean, default: false },
    },
    computed: {
        ...mapState("userAuth", ["user", "company"]),
        companies() {
            return this.user && this.user.companies ? this.user.companies : [];
        },
        hasMultipleCompanies() {
            return this.companies.length > 1;
        },
        activeCompanyId() {
            return this.company ? this.company.id : null;
        },
        activeCompanyName() {
            return this.company && this.company.name
                ? this.company.name
                : "Seleccionar empresa";
        },
        triggerTooltip() {
            return `Empresa: ${this.activeCompanyName}`;
        },
    },
    methods: {
        ...mapActions("userAuth", ["setActiveCompany"]),
        isActive(company) {
            return String(company.id) === String(this.activeCompanyId);
        },
        select(company) {
            // setActiveCompany recarga la página; evitamos el reload si el
            // usuario reelige la empresa que ya está activa.
            if (!this.isActive(company)) {
                this.setActiveCompany(company);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.company-dropdown {
    width: 100%;
    margin-top: 8px;
}

// Expandido: el trigger de Buefy (.dropdown-trigger) por defecto es inline;
// lo forzamos a 100% para que el combobox ocupe todo el ancho del sidebar.
.company-dropdown:not(.is-collapsed) ::v-deep .dropdown-trigger {
    width: 100%;
}

.company-dropdown.is-collapsed {
    display: flex;
    justify-content: center;
}

// Trigger ---------------------------------------------------------------
.company-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    background-color: transparent;
    border: 1px solid var(--sidebar-rule, rgba(16, 24, 40, 0.08));
    border-radius: var(--sidebar-radius, 6px);
    cursor: pointer;
    color: var(--sidebar-ink, #1d2939);
    transition: background-color var(--app-transition-fast),
        border-color var(--app-transition-fast);

    &:hover {
        background-color: var(--sidebar-hover, rgba(16, 24, 40, 0.03));
    }

    &:focus-visible {
        outline: 2px solid var(--sidebar-accent, #0d5154);
        outline-offset: -2px;
    }
}

.is-collapsed .company-trigger {
    width: 36px;
    justify-content: center;
    padding: 6px;
}

.company-glyph {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sidebar-ink-faint, #98a2b3);

    svg {
        width: 18px;
        height: 18px;
    }
}

.company-name {
    flex: 1;
    min-width: 0;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: var(--sidebar-ink, #1d2939);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.company-chevron {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: var(--sidebar-ink-faint, #98a2b3);
    transition: transform var(--app-transition-base);

    &.is-open {
        transform: rotate(180deg);
    }
}

// Panel -----------------------------------------------------------------
::v-deep .dropdown-content {
    background-color: var(--sidebar-surface, #ffffff);
    border: 1px solid var(--sidebar-rule, rgba(16, 24, 40, 0.06));
    border-radius: 14px;
    // Sombra en capas (soft + presencia), no dramática: una capa de contacto
    // corta y una capa ambiente difusa.
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.06),
        0 16px 40px -8px rgba(16, 24, 40, 0.22);
    padding: 6px;
    min-width: 248px;
    max-width: 320px;
}

::v-deep .company-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 12px;
    border-radius: 10px;
    font-size: 14px;
    color: var(--sidebar-ink, #1d2939);
    transition: background-color var(--app-transition-fast);

    &:hover {
        background-color: var(--sidebar-hover, rgba(16, 24, 40, 0.04));
    }

    &.is-active {
        font-weight: 600;
        color: var(--sidebar-accent, #0d5154);
        background-color: var(--sidebar-accent-soft, rgba(13, 81, 84, 0.08));
    }
}

.company-item-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-item-check {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: var(--sidebar-accent, #0d5154);
}
</style>
